import React, { Component } from 'react';
import moment from 'moment';
import TasksAPI from '../../../services/tasksService';
import ScopeService from '../../../services/scopeService';
import TaskOutcomeModal from '../../partial/TaskOutcomeModal';
import { isEngagementType, getTaskTypeMeta } from '../../../constants/taskTypeMeta';
import { formatPhoneDisplay, telHref } from '../../../util/phoneUtils';

// Tech-facing reminders list shown in MyWork's "Reminders" tab.
// Lists today's open tasks for the technician + location, with task-type-
// aware rendering. Tap "Mark done" (or task-specific CTA) to complete.

const PRIORITY_STYLES = {
  urgent: { color: '#DC2626', bg: '#FEE2E2', label: 'Urgent' },
  high:   { color: '#D97706', bg: '#FEF3C7', label: 'High' },
  normal: { color: '#6B7280', bg: '#F3F4F6', label: '' },
  low:    { color: '#9CA3AF', bg: '#F9FAFB', label: 'Low' },
};

class RemindersList extends Component {
  state = {
    tasks: [],
    loading: true,
    completingId: null,
    error: null,
    outcomeTask: null,
    outcomeError: null,
  };

  async componentDidMount() {
    await this.loadTasks();
  }

  async componentDidUpdate(prevProps) {
    if (prevProps.locationId !== this.props.locationId) {
      await this.loadTasks();
    }
  }

  loadTasks = async () => {
    const { installerId, locationId } = this.props;
    if (!installerId) {
      this.setState({ tasks: [], loading: false });
      return;
    }
    this.setState({ loading: true, error: null });
    try {
      const todayStr = moment().format('YYYY-MM-DD');
      const tasks = await TasksAPI.getAll({
        installerId,
        locationId: locationId || undefined,
        status: 'open,in_progress',  // server should accept comma-separated; falls back if not
        dueBefore: `${todayStr}T23:59:59`,
        limit: 50,
        userId: this.props.userId || ScopeService.getUserId(),
        userRole: ScopeService.getUserRole(),
      });
      // Sort: urgent > high > normal > low, then earliest due first
      const ordered = [...(tasks || [])].sort((a, b) => {
        const order = { urgent: 0, high: 1, normal: 2, low: 3 };
        const pa = order[a.priority] ?? 2;
        const pb = order[b.priority] ?? 2;
        if (pa !== pb) return pa - pb;
        return (a.due_date || '').localeCompare(b.due_date || '');
      });
      this.setState({ tasks: ordered, loading: false });
      if (this.props.onCountChange) {
        this.props.onCountChange(ordered.length);
      }
    } catch {
      this.setState({ tasks: [], loading: false, error: 'Could not load reminders.' });
    }
  };

  handleComplete = async (task, options) => {
    // Engagement tasks require an outcome — open the picker instead of completing directly
    if (isEngagementType(task.task_type) && !options?.resolution_type) {
      this.setState({ outcomeTask: task, outcomeError: null });
      return;
    }
    this.setState({ completingId: task.task_id || task.id });
    try {
      await TasksAPI.complete(task.task_id || task.id, this.props.userId, options);
      this.setState(prev => ({
        tasks: prev.tasks.filter(t => (t.task_id || t.id) !== (task.task_id || task.id)),
        completingId: null,
        outcomeTask: null,
      }), () => {
        if (this.props.onCountChange) {
          this.props.onCountChange(this.state.tasks.length);
        }
      });
    } catch (err) {
      if (this.state.outcomeTask) {
        const serverMsg = err.response?.data?.message || err.response?.data?.error;
        this.setState({
          completingId: null,
          outcomeError: serverMsg || 'Could not complete the task. Please choose an outcome and try again.'
        });
        return;
      }
      this.setState({ completingId: null });
    }
  };

  renderMaterialsCheckBody = (task) => {
    const shortages = task.metadata?.shortages || [];
    if (shortages.length === 0) {
      return (
        <p className="mb-2 text-muted" style={{ fontSize: 13 }}>
          <i className="fa-sharp fa-regular fa-circle-check mr-1 text-success" />
          All materials present. Confirm truck loaded.
        </p>
      );
    }
    return (
      <div className="mb-2" style={{ fontSize: 13 }}>
        <p className="mb-1 text-danger">
          <i className="fa-sharp fa-regular fa-triangle-exclamation mr-1" />
          <strong>{shortages.length} item{shortages.length !== 1 ? 's' : ''} short</strong>
        </p>
        <ul className="pl-3 mb-0">
          {shortages.map((s, idx) => (
            <li key={idx}>
              <strong>{s.product_name}</strong>: need {s.needed_qty}, have {s.on_hand_qty}
              {s.shortfall ? ` (short ${s.shortfall})` : ''}
              {Array.isArray(s.affected_requests) && s.affected_requests.length > 0 && (
                <span className="text-muted">
                  {' '}— {s.affected_requests.length} job{s.affected_requests.length !== 1 ? 's' : ''} affected
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  renderTaskBody = (task) => {
    if (task.task_type === 'materials_check') return this.renderMaterialsCheckBody(task);
    if (task.task_type === 'inbound_call') {
      return (
        <div className="mb-2" style={{ fontSize: 13 }}>
          {task.caller_phone_e164 && (
            <p className="mb-1">
              <a href={telHref(task.caller_phone_e164)} style={{ color: '#3B82F6', fontWeight: 600 }}>
                <i className="fa-sharp fa-regular fa-phone mr-1" />
                {formatPhoneDisplay(task.caller_phone_e164)}
              </a>
            </p>
          )}
          {task.description && (
            <p className="text-muted mb-0" style={{ whiteSpace: 'pre-line' }}>{task.description}</p>
          )}
        </div>
      );
    }
    if (task.task_type === 'consultation') {
      const reminderAt = task.reminder_at ? moment(task.reminder_at, 'YYYY-MM-DD HH:mm') : null;
      return (
        <div className="mb-2" style={{ fontSize: 13 }}>
          {reminderAt && reminderAt.isValid() && (
            <p className="mb-1">
              <i className="fa-sharp fa-regular fa-calendar-clock mr-1" />
              {reminderAt.format('MMM D [at] h:mm A')}
            </p>
          )}
          {task.description && <p className="text-muted mb-0">{task.description}</p>}
        </div>
      );
    }
    return task.description
      ? <p className="text-muted mb-2" style={{ fontSize: 13 }}>{task.description}</p>
      : null;
  };

  renderTaskCta = (task) => {
    const completingId = this.state.completingId;
    const id = task.task_id || task.id;
    const isCompleting = completingId === id;
    const isMaterials = task.task_type === 'materials_check';
    const label = isMaterials
      ? (task.metadata?.shortages?.length > 0 ? 'Acknowledged ✓' : 'Loaded ✓')
      : 'Mark done';
    return (
      <button
        type="button"
        className="btn btn-sm btn-primary"
        onClick={() => this.handleComplete(task)}
        disabled={isCompleting}
      >
        {isCompleting ? 'Saving…' : label}
      </button>
    );
  };

  render() {
    const { tasks, loading, error } = this.state;

    if (loading) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading…</span>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="container py-4">
          <div className="alert alert-warning">
            <i className="fa-sharp fa-regular fa-triangle-exclamation mr-2" />
            {error}
            <button className="btn btn-link btn-sm" onClick={this.loadTasks}>Retry</button>
          </div>
        </div>
      );
    }

    if (tasks.length === 0) {
      return (
        <div className="text-center text-muted py-5">
          <i className="fa-sharp fa-regular fa-circle-check" style={{ fontSize: 32 }} />
          <p className="mt-3">No reminders for today — you're all caught up.</p>
        </div>
      );
    }

    return (
      <div className="container py-3" style={{ maxWidth: 640 }}>
        <h4 className="mb-3">Today's Reminders</h4>
        {tasks.map(task => {
          const id = task.task_id || task.id;
          const pri = PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.normal;
          return (
            <div
              key={id}
              style={{
                background: '#ffffff',
                border: '1px solid #e5e7eb',
                borderLeft: `4px solid ${pri.color}`,
                borderRadius: 8,
                padding: 12,
                marginBottom: 8,
              }}
            >
              <div className="d-flex align-items-start mb-1">
                <div style={{ flex: 1 }}>
                  {isEngagementType(task.task_type) && (
                    <i
                      className={getTaskTypeMeta(task.task_type).icon}
                      title={getTaskTypeMeta(task.task_type).label}
                      style={{ color: getTaskTypeMeta(task.task_type).accent, marginRight: 6 }}
                    />
                  )}
                  <strong>{task.title}</strong>
                  {task.task_type === 'inbound_call' && task.call_count > 1 && (
                    <span
                      title={`Contacted ${task.call_count} times`}
                      style={{
                        background: '#FEE2E2', color: '#DC2626', padding: '1px 8px',
                        borderRadius: 12, fontSize: 11, fontWeight: 700, marginLeft: 8,
                      }}
                    >
                      &times;{task.call_count}
                    </span>
                  )}
                  {pri.label && (
                    <span
                      style={{
                        background: pri.bg,
                        color: pri.color,
                        padding: '1px 8px',
                        borderRadius: 12,
                        fontSize: 11,
                        fontWeight: 700,
                        marginLeft: 8,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}
                    >
                      {pri.label}
                    </span>
                  )}
                </div>
              </div>
              {this.renderTaskBody(task)}
              {task.next_action && task.task_type !== 'materials_check' && (
                <p className="mb-2" style={{ fontSize: 12, color: '#495057' }}>
                  <em>{task.next_action}</em>
                </p>
              )}
              {this.renderTaskCta(task)}
            </div>
          );
        })}

        {/* Outcome picker — required for engagement task completion */}
        <TaskOutcomeModal
          show={!!this.state.outcomeTask}
          task={this.state.outcomeTask}
          onHide={() => this.setState({ outcomeTask: null, outcomeError: null })}
          onConfirm={(options) => this.handleComplete(this.state.outcomeTask, options)}
          submitting={!!this.state.completingId}
          error={this.state.outcomeError}
        />
      </div>
    );
  }
}

export default RemindersList;
